import { isIdle, markExpired, renewSession, session } from '../auth/session.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Closed reason the API attaches to some refusals (e.g. `too_short` for a password). */
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const HTTP_UNAUTHORIZED = 401;

/**
 * Sends a request with the access token held in memory. On a 401, the session
 * is renewed once, unless the user has been idle past the inactivity setting,
 * and the request replayed; if renewal fails, the session is marked expired.
 */
async function authorizedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const send = () => {
    const token = session.accessToken();
    return fetch(`/api/v1${path}`, {
      ...init,
      headers: {
        // Declared only when there is a body: a bodyless POST that claims JSON
        // is rejected by the server as an empty payload.
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
  };

  let response = await send();
  if (response.status === HTTP_UNAUTHORIZED && !isIdle() && (await renewSession())) {
    response = await send();
  }
  if (response.status === HTTP_UNAUTHORIZED) markExpired();
  return response;
}

async function failure(response: Response): Promise<ApiError> {
  const body: unknown = await response.json().catch(() => null);
  const problem: Record<string, unknown> = typeof body === 'object' && body !== null ? { ...body } : {};
  const text = (key: string) => (typeof problem[key] === 'string' ? problem[key] : undefined);
  return new ApiError(
    response.status,
    text('code') ?? 'UNKNOWN',
    text('title') ?? response.statusText,
    text('detail'),
  );
}

/** Single fetch wrapper. Every request goes through here. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await authorizedFetch(path, init);
  if (!response.ok) throw await failure(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Fetches a file through the authenticated client and hands it to the browser
 * as a download, named by the server's `Content-Disposition`.
 */
export async function download(path: string): Promise<void> {
  const response = await authorizedFetch(path);
  if (!response.ok) throw await failure(response);
  const filename =
    /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ?? 'ederest-export';
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Calls the API without a session: activation and reset links. */
export async function publicApi(path: string, body: unknown): Promise<void> {
  const response = await fetch(`/api/v1${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await failure(response);
}
